import { dehydrate, QueryClient } from '@tanstack/react-query';
import { LandingPage } from 'components/pages/LandingPage';
import { fetchLandingPage, LANDING_PAGE_KEY } from 'hooks/pages/useLandingPage';
import { fetchLandingPages } from 'hooks/useLandingPages';
import { env } from 'lib/constants/env';
import type { PageContext } from 'types/common/PageContext';
import type { SiteCategory } from 'types/common/SiteCategory';
import {
  blocksBuildTimeResolver,
  prefetchCommonData,
  prefetchReviewsData,
} from 'utils/buildTime';
import { fetchWithSentry } from 'utils/pageTemplates/fetchWithSentry';

export function landingPage(category: SiteCategory) {
  const isSkincarePage = category === 'skincare';

  async function getStaticProps(context: PageContext) {
    const queryClient = new QueryClient();

    const landingPageData = await fetchWithSentry({
      queryClient,
      queryKey: [LANDING_PAGE_KEY, context.params.slug],
      queryFn: fetchLandingPage,
      tags: {
        template: 'landingPage',
        slug: context.params.slug,
        api: 'sanity',
      },
    });

    /* INFO: If isSkincarePath is toggled, only build if we are
     * on the skincare path. We end up building the same page
     * twice without this. */
    if (!landingPageData || landingPageData.isSkincarePath !== isSkincarePage) {
      return {
        notFound: true,
        revalidate: env.ISR_TTL_SECONDS,
      };
    }

    await prefetchCommonData(queryClient);
    await prefetchReviewsData(queryClient);
    await blocksBuildTimeResolver(queryClient, landingPageData.blocks);

    return {
      revalidate: env.ISR_TTL_SECONDS,
      props: {
        dehydratedState: dehydrate(queryClient),
      },
    };
  }

  const getStaticPaths = async () => {
    const landingPages = await fetchLandingPages();
    const paths = landingPages
      .filter(({ isSkincarePath }) => isSkincarePath === isSkincarePage)
      .map(({ slug }) => ({ params: { slug } }));

    /* Static build */
    return { paths, fallback: false };
  };

  const LandingPageComponent = () => <LandingPage />;

  return {
    getStaticProps,
    getStaticPaths,
    LandingPageComponent,
  };
}
